import React from "react";
import i18n from "../lib/i18n";
import { arrayOf, bool, string, shape, object, number } from "prop-types";
import { Bar } from "react-chartjs-2";
import {
  getColor,
  getLabel,
  sumBarLabel,
  tooltipColorSettings,
  barLegendSettings,
  eventSettings,
  seriesToChartData,
  barLabel,
} from "../lib/chartHelpers";
import { currency, emissions, energy } from "../lib/helpers";
import _ from "lodash";

const formatters = {
  currency,
  emissions,
  energy,
};

const TimeBarChart = ({
  series,
  devicePixelRatio,
  stacked = true,
  maxLegendItems = 100,
  reverseStackingOrder = false,
  height = 300,
  preferredColors = {},
  alignLegendItems = "center",
  formatter = "currency",
  lineData = {},
}) => {
  const datasets = series.map((serie, index) => ({
    id: serie.id,
    label: getLabel(serie.label),
    backgroundColor: getColor(index, getLabel(serie.label), preferredColors),
    borderWidth: 0,
    maxBarThickness: 50,
    data: seriesToChartData(serie.data),
  }));

  const getDatasetKey = (dataset) => {
    return dataset.id ? dataset.id : dataset.label;
  };

  const formattedValue = (value) => {
    const formatterFunction = formatters[formatter];
    return formatterFunction ? formatterFunction(value) : value;
  };

  const lineDataset = (lineData) => {
    if (!_.isPlainObject(lineData)) {
      throw new Error("TypeError: lineData needs to an object.");
    }

    return {
      type: "line",
      borderDash: [5, 5],
      label: lineData.label,
      tooltipLabel: lineData.tooltip_label,
      fill: false,
      borderColor: "#FCD483",
      pointRadius: 4,
      pointHoverRadius: 10,
      lineTension: 0,
      data: seriesToChartData(lineData.data),
    };
  };

  if (reverseStackingOrder) {
    datasets.reverse();
  }

  const chartDatasets = !_.isEmpty(lineData)
    ? [lineDataset(lineData), ...datasets]
    : datasets;

  return (
    <div style={{ height, marginTop: 20 }}>
      <Bar
        datasetKeyProvider={getDatasetKey}
        data={{ datasets: chartDatasets }}
        options={{
          layout: {
            padding: 5,
          },
          maintainAspectRatio: false,
          devicePixelRatio: devicePixelRatio,
          legend: {
            display: series.length > 1 && series.length < maxLegendItems,
            reverse: !_.isEmpty(lineData) || reverseStackingOrder,
            align: alignLegendItems,
            ...barLegendSettings,
          },
          ...eventSettings,
          tooltips: {
            position: "nearest",
            callbacks: {
              title: (items) =>
                `${i18n.t("js.helpers.year")} ${new Date(items[0].label).getFullYear()}`,
              label: (tooltipItem, data) =>
                barLabel(tooltipItem, data, formatters[formatter]),
              afterBody: (tooltipItems, data) => {
                if (data.datasets.length > 1 && stacked) {
                  const barData = {
                    ...data,
                    datasets: data.datasets.filter((d) => d.type !== "line"),
                  };
                  return sumBarLabel(
                    tooltipItems[0].index,
                    barData,
                    formatters[formatter]
                  );
                }
              },
            },
            ...tooltipColorSettings,
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                },
                type: "time",
                time: {
                  unit: "year",
                },
                distribution: "linear",
                offset: true,
                ticks: {
                  autoSkip: true,
                  source: "data",
                },
                stacked: stacked,
              },
            ],
            yAxes: [
              {
                stacked: stacked,
                ticks: {
                  callback: (value) => formattedValue(value),
                  maxTicksLimit: 4,
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default TimeBarChart;

TimeBarChart.propTypes = {
  series: arrayOf(shape({ data: object.isRequired, label: string })).isRequired,
  devicePixelRatio: number,
  stacked: bool,
  alignLegendItems: string,
};
